/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { DeliveryOption, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    NodeTag,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { buildDistanceString } from '../find-store-worldwide-location-line-item';

/**
 * Store Locator line item resources.
 */
export interface ISelectedStoreResources {
    closedText: string;
}

/**
 * Store Selector resources.
 */
export interface ISelectedStoreProps {
    className?: string;
    shouldHideStockStatus?: boolean;
    isCurrentLocation?: boolean;
    isInStock?: boolean;
    selectedStore?: IFullOrgUnitAvailability;
    resources: ISelectedStoreResources;
    stockStatusLabel?: string;
    isLocationDisabled?: boolean;
    telemetryContent?: ITelemetryContent;
    preferredDistanceUnit: string;
    storeRenderedComponent?: React.ReactNode;
    handlers: {
        onSelected(location: OrgUnitLocation, deliveryMode?: string): void;
        onSetAsPreferredStore(locationId: string | undefined): void;
        onRemovePreferredStore(): void;
        closeStoreDetailsModal(): Promise<void>;
    };
}

/**
 * Store Selector resources.
 */
export interface ISelectedStoreViewProps {
    isCurrentLocation?: boolean;
    canSelectLocation?: boolean;
    selectedStore?: IFullOrgUnitAvailability;
    deliveryOptions?: DeliveryOption[];
    resources: ISelectedStoreResources;
    storeRenderedComponent: React.ReactNode;
    storeDetailsMain: INodeProps;
    storeHeader: INodeProps;
    storeName: React.ReactNode;
    storeDistance: React.ReactNode;
    storeDetailsAvailabilityContainer: INodeProps;
    currentLocation: React.ReactNode;
    storeDetailsCloseButton?: React.ReactNode;
    handlers: {
        onSelected(location: OrgUnitLocation, deliveryMode?: string): void;
    };
}

/**
 * On store details close functionality.
 * @param props -Store locator location items.
 * @returns Void promise.
 */
const storeDetailsModalHandler = (props: ISelectedStoreProps) => async (): Promise<void> => {
    await props.handlers.closeStoreDetailsModal();
};

/**
 * Renders selected store details.
 * @param props - Store location details props.
 * @returns - Selected Store view props.
 */
const selectedStoreDetails = (props: ISelectedStoreProps): ISelectedStoreViewProps => {
    const {
        className,
        shouldHideStockStatus,
        isCurrentLocation,
        isInStock,
        selectedStore,
        resources,
        isLocationDisabled,
        telemetryContent,
        preferredDistanceUnit,
        storeRenderedComponent
    } = props;

    const parentClassName = className ? className : 'ms-store-select-venchi__map__selected_store';
    const storeName: string | undefined = selectedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName;
    const location = selectedStore?.OrgUnitAvailability?.OrgUnitLocation;
    const distanceAsString: string | undefined = buildDistanceString(location?.Distance, preferredDistanceUnit, isLocationDisabled);
    // eslint-disable-next-line no-nested-ternary -- Required to check stock Label
    const canSelectLocation = !shouldHideStockStatus && isInStock;
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    payLoad.contentAction.etext = TelemetryConstant.PreferredStore;
    payLoad.contentAction.etext = TelemetryConstant.RemovePreferredStore;

    const viewProps: ISelectedStoreViewProps = {
        storeRenderedComponent,
        selectedStore,
        isCurrentLocation,
        canSelectLocation,
        resources,
        storeDetailsMain: {
            tag: 'div' as NodeTag,
            className: `${parentClassName}__main`
        },
        storeHeader: {
            tag: 'div' as NodeTag,
            className: `${parentClassName}__header`
        },
        storeName: <span className={`${parentClassName}__header_store_name`}>{storeName}</span>,
        storeDetailsCloseButton: <Button className={`${parentClassName}__header_close_button`} onClick={storeDetailsModalHandler(props)} />,
        // eslint-disable-next-line prettier/prettier
        storeDistance: distanceAsString ? <span className={`${parentClassName}__header_store-distance`}> {distanceAsString}</span> : '',
        storeDetailsAvailabilityContainer: {
            tag: 'div' as NodeTag,
            className: `${parentClassName}__container`
        },
        //TODO restore currentLocation
        currentLocation: <p className={`${parentClassName}__container_current-location`}>{''}</p>,
        handlers: {
            onSelected: props.handlers.onSelected
        }
    };

    return viewProps;
};

/**
 * Store location line item component.
 */
export const SelectedStoreComponent = selectedStoreDetails as (props: ISelectedStoreProps) => ISelectedStoreViewProps;
